/* LT-12 + LT-13 — Export modal.
 *
 * Two sections inside a single Shadcn AlertDialog:
 *   1) CLIENT DOCUMENTS — three-way RadioGroup (LT-12). Single primary CTA
 *      at the bottom: "Generate PDF" — fires the selected radio option.
 *   2) INTERNAL & SUPPLIER REPORTS — two stand-alone widgets (LT-13):
 *      a) Materials & Allowances Report → [Download PDF] [Download CSV]
 *         (independent action buttons, own loading states + toast lifecycle)
 *      b) Supplier Enquiry Report       → [Configure & Download] (opens
 *         the SupplierEnquiryDrawer mounted by the parent)
 *
 * Decision Q6 (locked at pre-flight): keep the bottom "Generate PDF" CTA
 * scoped to Client Documents only; the new section uses its own buttons.
 */
import React, { useEffect, useState } from "react";
import { FileDown, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  RadioGroup, RadioGroupItem,
} from "./ui/radio-group";
import { Label } from "./ui/label";

// LT-12.5 + LT-26 — Client Documents (4 radios) and Internal Documents
// (1 radio: LUX Copy). The two RadioGroup sections share a single
// `outputType` state so picking an Internal doc deselects any Client doc
// and vice-versa (locked spec: only one PDF generates per click).
// LT-16 Part 4c — Client-facing outputs are now issued exclusively via
// Adjudication → Create Client Estimate. The header Export dialog is
// Chris's internal-only working-copy path (LUX Copy + CSVs). Any
// output visible in Create Client Estimate MUST NOT appear here, or
// the "single commit for a client PDF" invariant slips.
const CLIENT_OUTPUTS = [];

const INTERNAL_OUTPUTS = [
  {
    value: "lux_copy",
    title: "LUX Copy (internal)",
    detail: "Estimator's working copy — same as the Detailed Estimate plus Internal Cost, Margin %, Hours and Trade columns. Do not share with the client.",
    badge: "INTERNAL",
  },
];


function _filenameFromHeaders(resp, fallback) {
  const x = resp.headers?.["x-pdf-filename"];
  if (x) return x;
  const cd = resp.headers?.["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/i.exec(cd);
  return (m && m[1]) || fallback;
}

function _downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.style.display = "none";
  document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
}

async function _readBlobError(err, fallback) {
  let detail = fallback;
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const txt = await data.text();
      const parsed = JSON.parse(txt);
      if (typeof parsed?.detail === "string") detail = parsed.detail;
    } catch { /* keep generic */ }
  } else if (typeof data?.detail === "string") {
    detail = data.detail;
  }
  return detail;
}


export default function ExportPdfDialog({
  open, onOpenChange, tenderId, tenderRef, onOpenSupplierEnquiry,
}) {
  const [outputType, setOutputType] = useState("full_estimate_pack");
  const [generating, setGenerating] = useState(false);
  const [downloadingMat, setDownloadingMat] = useState(null);  // "pdf"|"csv"|null
  // Quick existence check — if the tender has zero materials, disable the
  // materials report + supplier enquiry CTAs and surface a clear hint.
  const [hasMaterials, setHasMaterials] = useState(null);  // null=loading, true/false=resolved
  // LT-26 — £0 Pro Sums warning shown above the Generate button if any
  // Pro Sum line is at £0 (informational; doesn't block generation).
  const [zeroProSumCount, setZeroProSumCount] = useState(0);
  const [proSumTotal, setProSumTotal] = useState(null);

  // Reset state on every open so radio + download flags don't persist
  // stale-active across sessions. (Tester nit from LT-12 iter_21.)
  useEffect(() => {
    if (!open) return;
    setOutputType("full_estimate_pack");
    setGenerating(false);
    setDownloadingMat(null);
    setHasMaterials(null);
    setZeroProSumCount(0);
    setProSumTotal(null);
    api.get(`/tenders/${tenderId}/materials-aggregated`)
      .then((r) => {
        const total = (r.data?.categories || []).reduce(
          (sum, c) => sum + (c.items?.length || 0), 0,
        );
        setHasMaterials(total > 0);
      })
      .catch(() => setHasMaterials(false));
    // LT-26 — surface count of £0 Pro Sums for the export warning row.
    api.get(`/tenders/${tenderId}/provsums`)
      .then((r) => {
        const its = r.data?.items || [];
        setZeroProSumCount(its.filter((it) => Number(it.value || 0) === 0).length);
        setProSumTotal(r.data?.summary?.grand_total || 0);
      })
      .catch(() => { setZeroProSumCount(0); setProSumTotal(null); });
  }, [open, tenderId]);

  /* ----- Client Documents — primary CTA ----- */
  const handleGenerate = async () => {
    setGenerating(true);
    const toastId = toast.loading("Generating PDF…");
    try {
      const resp = await api.post(
        `/tenders/${tenderId}/export-pdf`,
        { output_type: outputType },
        { responseType: "blob" },
      );
      const filename = _filenameFromHeaders(resp, `${tenderRef || "tender"}.pdf`);
      _downloadBlob(resp.data, filename);
      toast.success("PDF ready", {
        id: toastId, description: filename, duration: 4000,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(await _readBlobError(err, "Failed to generate PDF"), { id: toastId });
    } finally {
      setGenerating(false);
    }
  };

  /* ----- Materials Report — independent download buttons ----- */
  const handleMaterialsDownload = async (format) => {
    setDownloadingMat(format);
    const toastId = toast.loading(`Generating Materials Report (${format.toUpperCase()})…`);
    try {
      const resp = await api.post(
        `/tenders/${tenderId}/exports/materials-report`,
        { format },
        { responseType: "blob" },
      );
      const filename = _filenameFromHeaders(
        resp, `${tenderRef || "tender"}_materials.${format}`,
      );
      _downloadBlob(resp.data, filename);
      toast.success("Materials Report ready", {
        id: toastId, description: filename, duration: 4000,
      });
    } catch (err) {
      toast.error(
        await _readBlobError(err, "Failed to generate Materials Report"),
        { id: toastId },
      );
    } finally {
      setDownloadingMat(null);
    }
  };

  /* ----- Supplier Enquiry — open the drawer (parent handles state) ----- */
  const handleSupplierConfigure = () => {
    onOpenChange(false);            // close modal first
    // Let the dialog finish animating out before opening the drawer so
    // Radix's focus traps don't fight each other.
    setTimeout(() => onOpenSupplierEnquiry?.(), 200);
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!generating && !downloadingMat) onOpenChange(o); }}>
      <AlertDialogContent className="max-w-xl" data-testid="export-pdf-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Export</AlertDialogTitle>
          <AlertDialogDescription>
            Choose a document or report to generate for{" "}
            <strong>{tenderRef || "this tender"}</strong>.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* LT-26 — Inline warning row for £0 Pro Sums (informational). */}
        {zeroProSumCount > 0 ? (
          <div
            className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2"
            data-testid="export-pdf-zero-prosum-warning"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-700 shrink-0">
              <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span className="text-amber-900" style={{ fontSize: 11.5 }}>
              <strong>{zeroProSumCount}</strong> Pro Sum line{zeroProSumCount === 1 ? "" : "s"} at £0 on this tender — they'll appear in the document but contribute nothing to the total.
            </span>
          </div>
        ) : null}

        {/* =====  Section 1 — Client Documents (radio) =================== */}
        <SectionHeading title="Client Documents" />
        <RadioGroup
          value={outputType}
          onValueChange={setOutputType}
          className="space-y-2"
          data-testid="export-pdf-options"
        >
          {CLIENT_OUTPUTS.map((opt) => (
            <RadioOption key={opt.value} opt={opt} selected={outputType === opt.value} />
          ))}
        </RadioGroup>

        {/* =====  Section 2 — Internal Documents (radio, shares state) === */}
        <SectionHeading title="Internal Documents" />
        <RadioGroup
          value={outputType}
          onValueChange={setOutputType}
          className="space-y-2"
          data-testid="export-pdf-internal-options"
        >
          {INTERNAL_OUTPUTS.map((opt) => (
            <RadioOption key={opt.value} opt={opt} selected={outputType === opt.value} />
          ))}
        </RadioGroup>

        {/* =====  Section 3 — Reports (independent download buttons) ===== */}
        <SectionHeading title="Reports" />
        <div className="space-y-2">

          {/* Materials & Allowances Report */}
          <div
            className="rounded-md border border-line bg-surface px-3 py-2.5"
            data-testid="export-materials-row"
          >
            <div className="flex items-start gap-3">
              <FileDown size={14} className="text-ink-3 mt-0.5" />
              <div className="flex-1">
                <div className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
                  Materials &amp; Allowances Report
                </div>
                <div className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
                  Internal-facing, unbranded, full materials list with rates and totals.
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <ActionButton
                  onClick={() => handleMaterialsDownload("pdf")}
                  loading={downloadingMat === "pdf"}
                  disabled={!hasMaterials || downloadingMat !== null}
                  label="PDF"
                  testId="export-materials-pdf"
                />
                <ActionButton
                  onClick={() => handleMaterialsDownload("csv")}
                  loading={downloadingMat === "csv"}
                  disabled={!hasMaterials || downloadingMat !== null}
                  label="CSV"
                  variant="quiet"
                  testId="export-materials-csv"
                />
              </div>
            </div>
          </div>

          {/* Supplier Enquiry Report */}
          <div
            className="rounded-md border border-line bg-surface px-3 py-2.5"
            data-testid="export-supplier-row"
          >
            <div className="flex items-start gap-3">
              <Mail size={14} className="text-ink-3 mt-0.5" />
              <div className="flex-1">
                <div className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
                  Supplier Enquiry Report
                </div>
                <div className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
                  External-facing, no rates. Toggle materials per supplier and download PDF or CSV.
                </div>
              </div>
              <button
                type="button"
                onClick={handleSupplierConfigure}
                disabled={!hasMaterials || downloadingMat !== null || generating}
                className={[
                  "h-8 px-3 rounded-md border border-line bg-surface flex items-center gap-1.5",
                  "text-ink-2 hover:bg-surface-2/60",
                  (!hasMaterials || downloadingMat !== null || generating) && "opacity-50 cursor-not-allowed",
                ].filter(Boolean).join(" ")}
                style={{ fontSize: 12 }}
                data-testid="export-supplier-configure"
              >
                Configure &amp; Download
              </button>
            </div>
          </div>

          {hasMaterials === false && (
            <div className="text-ink-4 italic" style={{ fontSize: 11.5 }}>
              No materials in this tender yet — add at least one line item with a material cost to enable these reports.
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={generating || downloadingMat !== null}
            data-testid="export-pdf-cancel"
          >
            Close
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); handleGenerate(); }}
            disabled={generating || downloadingMat !== null}
            data-testid="export-pdf-generate"
          >
            {generating ? (
              <>
                <Loader2 size={13} className="mr-1.5 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <FileDown size={13} className="mr-1.5" />
                Generate PDF
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


function SectionHeading({ title }) {
  return (
    <div className="col-head mt-2 mb-2" style={{ fontSize: 10 }}>
      {title}
    </div>
  );
}


function ActionButton({ onClick, loading, disabled, label, variant = "primary", testId }) {
  const base = "h-8 px-3 rounded-md flex items-center gap-1.5";
  if (variant === "quiet") {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={[
          base,
          "border border-line bg-surface text-ink-2 hover:bg-surface-2/60",
          disabled && "opacity-50 cursor-not-allowed",
        ].filter(Boolean).join(" ")}
        style={{ fontSize: 12 }}
        data-testid={testId}
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
        {loading ? "…" : label}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        base,
        "text-white shadow-soft",
        disabled && "opacity-50 cursor-not-allowed",
      ].filter(Boolean).join(" ")}
      style={{ fontSize: 12, fontWeight: 500, background: "#2A3B4D" }}
      data-testid={testId}
    >
      {loading ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
      {loading ? "…" : label}
    </button>
  );
}


/* LT-26 — Single radio option row. Shared between the Client Documents
 * and Internal Documents sections. Lifted out of the section render so
 * the two RadioGroups share the same row markup. */
function RadioOption({ opt, selected }) {
  return (
    <label
      htmlFor={`export-pdf-${opt.value}`}
      className={[
        "flex items-start gap-3 rounded-md border border-line bg-surface px-3 py-2.5 cursor-pointer",
        "hover:bg-surface-2/40",
        selected && "border-accent bg-accent/5",
      ].filter(Boolean).join(" ")}
      data-testid={`export-pdf-option-${opt.value}`}
    >
      <RadioGroupItem
        value={opt.value}
        id={`export-pdf-${opt.value}`}
        className="mt-0.5"
      />
      <span className="flex-1">
        <div className="flex items-center gap-2">
          <Label
            htmlFor={`export-pdf-${opt.value}`}
            className="text-ink"
            style={{ fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            {opt.title}
          </Label>
          {opt.badge ? (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded font-mono tnum"
              style={{
                background: opt.badge === "INTERNAL" ? "#FCE7E7" : "#F4E6CE",
                color: opt.badge === "INTERNAL" ? "#9F2A2A" : "#B97E2A",
                border: opt.badge === "INTERNAL" ? "0.5px solid #E84A4A" : "0.5px solid #E8A34A",
                fontSize: 8.5,
                fontWeight: 600,
                letterSpacing: "0.05em",
              }}
              data-testid={`export-pdf-badge-${opt.value}`}
            >
              {opt.badge}
            </span>
          ) : null}
        </div>
        <div className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
          {opt.detail}
        </div>
      </span>
    </label>
  );
}
